<template>
  <div class="wrapper">
    <!-- Page header -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">Licences and certificates</h1>
            <p class="text-muted mb-0">Keep your business licences in one place and get reminded before they expire</p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton color="light" class="mr-2" aria-label="Refresh" title="Refresh" @click="refreshTable">
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add certificate
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard class="border-0">
      <!-- First load -->
      <CCardBody v-if="loading && !items.length" class="text-center py-5">
        <CSpinner color="primary" />
        <p class="text-muted mt-3 mb-0">Loading certificates…</p>
      </CCardBody>

      <!-- Error -->
      <CCardBody v-else-if="loadError && !items.length" class="text-center py-5">
        <CIcon name="cil-warning" size="3xl" class="mb-3 text-danger" />
        <h2 class="h5">Couldn't load certificates</h2>
        <p class="text-muted mb-3">Check your connection and try again.</p>
        <CButton color="primary" @click="refreshTable">
          <CIcon name="cil-reload" /> Try again
        </CButton>
      </CCardBody>

      <!-- Empty -->
      <CCardBody v-else-if="!items.length" class="text-center py-5">
        <CIcon name="cil-description" size="3xl" class="mb-3 text-muted" />
        <h2 class="h5">No certificates yet</h2>
        <p class="text-muted mb-3">Add a licence or certificate with its expiry date and we'll remind you before it runs out.</p>
        <CButton color="primary" @click="addNew">
          <CIcon name="cil-plus" /> Add certificate
        </CButton>
      </CCardBody>

      <template v-else>
        <CProgress v-if="loading" :value="100" animated color="primary" size="xs" />
        <!-- Desktop table -->
        <CCardBody class="d-none d-lg-block">
          <CDataTable
            :items="rows"
            :fields="fields"
            items-per-page-select
            :items-per-page="25"
            hover
            sorter
            :sorter-value="{ column: 'expiryDate', asc: true }"
            pagination
            responsive
          >
            <template #name="{ item }">
              <td class="py-2">
                <router-link :to="`/tenants/Certificate/${item.id}`" class="font-weight-bold">{{ item.name }}</router-link>
              </td>
            </template>
            <template #issuedDate="{ item }">
              <td class="py-2">{{ formatDate(item.issuedDate) }}</td>
            </template>
            <template #expiryDate="{ item }">
              <td class="py-2">
                {{ formatDate(item.expiryDate) }}
                <CBadge v-if="item.isExpired" color="danger" class="ml-1">Expired</CBadge>
              </td>
            </template>
            <template #reminderDate="{ item }">
              <td class="py-2">{{ formatDate(item.reminderDate) }}</td>
            </template>
            <template #document_link="{ item }">
              <td class="py-2">
                <CLink v-if="item.documentUrl" target="_blank" :href="item.documentUrl">{{ item.documentName }}</CLink>
                <span v-else class="text-muted">None</span>
              </td>
            </template>
            <template #actions="{ item }">
              <td class="py-2 text-right text-nowrap">
                <CButton color="light" size="sm" class="border" :aria-label="`Edit ${item.name}`" title="Edit" @click="onEdit(item)">
                  <CIcon name="cil-pencil" />
                </CButton>
                <CButton color="light" size="sm" class="border text-danger ml-1" :aria-label="`Delete ${item.name}`" title="Delete" @click="showDeleteConfirmation(item)">
                  <CIcon name="cil-trash" />
                </CButton>
              </td>
            </template>
          </CDataTable>
        </CCardBody>

        <!-- Phone / tablet cards -->
        <CCardBody class="d-lg-none p-2">
          <div v-for="item in rows" :key="item.id" class="border rounded p-3 mb-2">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <router-link :to="`/tenants/Certificate/${item.id}`" class="font-weight-bold">{{ item.name }}</router-link>
                <small class="d-block text-muted">{{ item.documentType }}</small>
              </div>
              <CDropdown color="light" placement="bottom-end">
                <template #toggler-content>
                  <CIcon name="cil-options" />
                  <span class="sr-only">More actions</span>
                </template>
                <CDropdownItem @click="onEdit(item)">
                  <CIcon name="cil-pencil" class="mr-2" /> Edit
                </CDropdownItem>
                <CDropdownItem v-if="item.documentUrl" :href="item.documentUrl" target="_blank">
                  <CIcon name="cil-file" class="mr-2" /> Open document
                </CDropdownItem>
                <CDropdownDivider />
                <CDropdownItem class="text-danger" @click="showDeleteConfirmation(item)">
                  <CIcon name="cil-trash" class="mr-2" /> Delete
                </CDropdownItem>
              </CDropdown>
            </div>
            <div class="small mt-2">
              <span class="text-muted">Expires</span> {{ formatDate(item.expiryDate) || "—" }}
              <CBadge v-if="item.isExpired" color="danger" class="ml-1">Expired</CBadge>
            </div>
          </div>
        </CCardBody>
      </template>
    </CCard>

    <CModal
      :title="`Delete ${itemToDelete.name || 'this certificate'}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
    >
      <p class="mb-0">
        The certificate and its attached document will be removed, and you won't get expiry reminders for it.
        This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">Keep it</CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          <CSpinner v-if="deleting" size="sm" class="mr-1" /> Delete certificate
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import CertificateApi from "@/lib/certificateApi";
import { formatDate, asDateString, mytToday } from "@/utils/datetime";

const fields = [
  { key: "name", label: "Name" },
  { key: "documentType", label: "Type" },
  { key: "issuedDate", label: "Issued" },
  { key: "expiryDate", label: "Expires" },
  { key: "reminderDate", label: "Reminder" },
  { key: "document_link", label: "Document", sorter: false, filter: false },
  { key: "actions", label: "", _style: "width:1%", sorter: false, filter: false },
];

export default {
  name: "CertificateList",
  data() {
    return {
      loading: true,
      loadError: false,
      items: [],
      fields,
      api: new CertificateApi(),
      warningModal: false,
      deleting: false,
      itemToDelete: {},
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Documentation", "Licences and certificates"]);
    this.refreshTable();
  },
  computed: {
    rows() {
      const today = mytToday();
      return this.items.map((item) => {
        const expiry = asDateString(item.expiryDate);
        return {
          ...item,
          // yyyy-MM-dd strings sort correctly in the table
          issuedDate: asDateString(item.issuedDate),
          expiryDate: expiry,
          reminderDate: asDateString(item.reminderDate),
          isExpired: !!expiry && expiry < today,
          documentName: this.getDocumentName(item),
          documentUrl: this.getDocumentUrl(item),
        };
      });
    },
  },
  methods: {
    formatDate,
    getDocumentName(item) {
      return (item.document && item.document.fileName) || "Open document";
    },
    getDocumentUrl(item) {
      const docs = item.certificateDocuments || [];
      return docs.length ? apiUrl + "documents/file/" + docs[0].documentId : "";
    },
    refreshTable() {
      this.loading = true;
      this.loadError = false;
      this.api
        .getListByCurrentBusiness()
        .then((response) => {
          this.items = response.result || [];
        })
        .catch((err) => {
          console.error("Couldn't load certificates", err);
          this.loadError = true;
          if (this.items.length) {
            this.$toast.error("Couldn't refresh certificates. Check your connection and try again.");
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onEdit(item) {
      this.$router.push({ path: `/tenants/Certificate/${item.id}` });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    confirmDelete() {
      this.deleting = true;
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.$toast.success("Certificate deleted");
          this.warningModal = false;
          this.itemToDelete = {};
          this.refreshTable();
        })
        .catch(({ data }) => {
          this.$toast.error(`Couldn't delete the certificate. ${helper.getErrorMessage(data)}`);
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    addNew() {
      this.$router.push({ path: "/tenants/Certificate" });
    },
  },
};
</script>
